import { Injectable } from '@angular/core';

export const EPF_CONSTRAINTS = {
  basicSalary:       { min: 5_000, max: 1_000_000, step: 500 },
  currentBalance:    { min: 0,     max: 100_000_000, step: 1_000 },
  employeePercent:   { min: 1,     max: 100,        step: 0.01 },
  employerPercent:   { min: 1,     max: 100,        step: 0.01 },
  interestRate:      { min: 1,     max: 15,         step: 0.01 },
  salaryGrowth:      { min: 0,     max: 20,         step: 0.5  },
  age:               { min: 18,    max: 59,         step: 1    }
} as const;

export interface EpfYearRow {
  year: number;
  age: number;
  monthlyBasic: number;
  employeeContribution: number;
  employerContribution: number;
  balance: number;
}

export interface EpfResult {
  years: EpfYearRow[];
  totalEmployeeContribution: number;
  totalEmployerContribution: number;
  totalInterest: number;
  maturityValue: number;
}

@Injectable({ providedIn: 'root' })
export class EpfCalculatorService {

  calculate(
    currentAge: number,
    retirementAge: number,
    monthlyBasicSalary: number,
    salaryGrowthPercent: number,
    employeePercent: number,
    employerPercent: number,
    annualInterestRate: number,
    currentBalance: number
  ): EpfResult {
    const monthlyRate = annualInterestRate / 100 / 12;
    const totalYears = retirementAge - currentAge;

    let balance = currentBalance;
    let basic = monthlyBasicSalary;
    let totalEmployee = 0;
    let totalEmployer = 0;

    const years: EpfYearRow[] = [];

    for (let y = 1; y <= totalYears; y++) {
      let yearEmployee = 0;
      let yearEmployer = 0;

      for (let m = 1; m <= 12; m++) {
        const employeeContribution = basic * (employeePercent / 100);
        const employerContribution = basic * (employerPercent / 100);
        balance += employeeContribution + employerContribution;
        balance *= (1 + monthlyRate);
        yearEmployee += employeeContribution;
        yearEmployer += employerContribution;
      }

      totalEmployee += yearEmployee;
      totalEmployer += yearEmployer;

      years.push({
        year: y,
        age: currentAge + y,
        monthlyBasic: basic,
        employeeContribution: yearEmployee,
        employerContribution: yearEmployer,
        balance
      });

      basic *= (1 + salaryGrowthPercent / 100);
    }

    const maturityValue = years.length ? years[years.length - 1].balance : currentBalance;
    const totalContribution = totalEmployee + totalEmployer;

    return {
      years,
      totalEmployeeContribution: totalEmployee,
      totalEmployerContribution: totalEmployer,
      totalInterest: maturityValue - currentBalance - totalContribution,
      maturityValue
    };
  }
}
